import { useState } from 'react';

import { Person } from '@api/lendingAPI/CustomerStateAPIHelper';
import { IconCheckDefault, IconFileClock } from '@iwoca/icons';
import { Pill, Text, Button, Modal } from '@iwoca/orion';
import { upperFirst } from 'lodash';
import { FormattedMessage } from 'react-intl';

import { buildAddressLabel } from '../../../../../../forms/autoAddress/AutoAddressHelper';
import { FormattedName } from '../../../../../../iwIntlProvider/FormattedName';

type TShareholderCardProps = {
    person: Person;
    status: 'confirmed' | 'incomplete' | 'needs_confirming';
    onEdit?: () => void;
    onRemove?: () => void;
    onConfirm?: () => void;
    isConfirming?: boolean;
    showAsShareholder?: boolean;
    disabled?: boolean;
};

export const ShareholderCard = ({
    person,
    status,
    onEdit,
    onRemove,
    onConfirm,
    isConfirming,
    showAsShareholder,
    disabled
}: TShareholderCardProps) => {
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const isConfirmed = status === 'confirmed';
    const needsConfirming = status === 'needs_confirming';
    const address = person.addresses?.[0];
    const dob = person.dateOfBirth;

    return (
        <div className="mb-l rounded-s border border-structure-80 p-l">
            <Pills person={person} showAsShareholder={showAsShareholder} />

            <div className="mt-m flex gap-m">
                <div className="hidden min-m:block">
                    {isConfirmed ? (
                        <IconCheckDefault className="shrink-0 text-l text-success-drk" />
                    ) : (
                        <IconFileClock className="shrink-0 text-l text-warning-drk" />
                    )}
                </div>
                <div className="flex flex-col gap-m">
                    <Text className="text-s font-med">
                        {person.title && `${upperFirst(person.title)} `}
                        <FormattedName
                            firstName={person.firstName}
                            lastName={person.lastName}
                        />
                    </Text>

                    {dob?.month && (
                        <Text className="text-s font-med">
                            {dob.day && `${dob.day}/`}
                            {dob.month}/{dob.year}
                        </Text>
                    )}

                    {address && (
                        <Text className="text-s font-med">
                            {buildAddressLabel(address)}
                        </Text>
                    )}
                </div>
            </div>

            {(onEdit || onRemove || onConfirm) && (
                <div className="mt-m border-t border-structure-80 pt-m">
                    <div className="flex items-center justify-between">
                        {needsConfirming && onConfirm ? (
                            <Button
                                type="button"
                                variant="tertiary"
                                className="h-auto p-0 disabled:border-none disabled:bg-transparent"
                                size="small"
                                onClick={onConfirm}
                                disabled={disabled || isConfirming}
                            >
                                <FormattedMessage
                                    id="_.funding_requirements.shareholders.confirm"
                                    defaultMessage="Confirm"
                                />
                            </Button>
                        ) : (
                            onEdit && (
                                <Button
                                    type="button"
                                    variant="tertiary"
                                    className="h-auto p-0 disabled:border-none disabled:bg-transparent"
                                    size="small"
                                    onClick={onEdit}
                                    disabled={disabled}
                                >
                                    {isConfirmed ? (
                                        <FormattedMessage
                                            id="_.funding_requirements.shareholders.edit"
                                            defaultMessage="Edit"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="_.funding_requirements.shareholders.complete_details"
                                            defaultMessage="Complete details"
                                        />
                                    )}
                                </Button>
                            )
                        )}
                        {onRemove && (
                            <Button
                                type="button"
                                variant="tertiary"
                                className="h-auto p-0"
                                size="small"
                                onClick={() => setIsRemoveModalOpen(true)}
                                disabled={disabled}
                            >
                                <FormattedMessage
                                    id="_.funding_requirements.shareholders.remove"
                                    defaultMessage="Remove"
                                />
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {onRemove && (
                <Modal
                    isOpen={isRemoveModalOpen}
                    onClose={() => setIsRemoveModalOpen(false)}
                    closeIcon
                    headerVariant="highlighted"
                    title={
                        <FormattedMessage
                            id="_.funding_requirements.shareholders.remove_modal.title"
                            defaultMessage="Remove shareholder details"
                        />
                    }
                >
                    <Text className="mb-l mt-s font-bold">
                        <FormattedMessage
                            id="_.funding_requirements.shareholders.remove_modal.body"
                            defaultMessage="Are you sure you want to remove this director or major shareholder?"
                        />
                    </Text>
                    <Button
                        variant="primary-rounded"
                        className="mt-l w-full"
                        onClick={() => {
                            onRemove();
                            setIsRemoveModalOpen(false);
                        }}
                    >
                        <FormattedMessage
                            id="_.funding_requirements.shareholders.remove_modal.confirm"
                            defaultMessage="Confirm"
                        />
                    </Button>
                </Modal>
            )}
        </div>
    );
};

const Pills = ({
    person,
    showAsShareholder
}: {
    person: Person;
    showAsShareholder?: boolean;
}) => {
    const ROLE_PILLS = [
        {
            key: 'isApplicant',
            variant: 'green',
            label: (
                <FormattedMessage
                    id="_.funding_requirements.shareholders.pill.this_is_you"
                    defaultMessage="This is you"
                />
            )
        },
        {
            key: 'isGuarantor',
            variant: 'london',
            label: (
                <FormattedMessage
                    id="_.funding_requirements.shareholders.pill.guarantor"
                    defaultMessage="Guarantor"
                />
            )
        },
        {
            key: 'isDirector',
            variant: 'yellow',
            label: (
                <FormattedMessage
                    id="_.funding_requirements.shareholders.pill.director"
                    defaultMessage="Director"
                />
            )
        }
    ] as const;

    return (
        <div className="flex flex-wrap gap-s">
            {ROLE_PILLS.filter(({ key }) => person[key]).map(
                ({ key, variant, label }) => (
                    <Pill key={key} variant={variant} size="small">
                        {label}
                    </Pill>
                )
            )}
            {person.isShareholder || showAsShareholder ? (
                <Pill variant="red" size="small">
                    <FormattedMessage
                        id="_.funding_requirements.shareholders.pill.major_shareholder"
                        defaultMessage="Major Shareholder"
                    />
                </Pill>
            ) : (
                <span className="rounded-full border border-structure-80 px-s py-2xs text-s text-structure-40">
                    <FormattedMessage
                        id="_.funding_requirements.shareholders.pill.not_major_shareholder"
                        defaultMessage="Not Major Shareholder"
                    />
                </span>
            )}
        </div>
    );
};
